import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n, { supportedLanguages } from '../i18n';

const SITE_NAME = 'allowtru';
const SITE_URL = 'https://allowtru.com';
const SUPPORT_EMAIL = 'support@allowtru.com';
const DEFAULT_ROBOTS = 'noindex,nofollow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';
const INDEXABLE_ROBOTS = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';

const LOCALE_MAP = {
  zh: 'zh_CN',
  'zh-TW': 'zh_TW',
  en: 'en_US',
  ja: 'ja_JP',
  es: 'es_ES',
  ko: 'ko_KR',
  fr: 'fr_FR',
  ar: 'ar_SA',
  de: 'de_DE',
  pt: 'pt_PT',
};

const PRIVATE_ROUTE_PREFIXES = [
  '/dashboard',
  '/devices',
  '/device-groups',
  '/policies',
  '/activities',
  '/logs',
  '/audit-logs',
  '/agents',
  '/tenants',
  '/settings',
  '/profile',
  '/support-tickets',
  '/support-center',
];

const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const trimDescription = (value, maxLength = 180) => {
  const text = normalizeText(value);
  if (!text || text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength - 1).trimEnd()}...`;
};

const toAbsoluteUrl = (path) => {
  const cleanPath = path && path !== '/' ? path : '';
  return `${SITE_URL}${cleanPath}`;
};

const ensureMetaTag = ({ name, property, content }) => {
  if (!content) {
    return;
  }

  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let meta = document.head.querySelector(selector);
  if (!meta) {
    meta = document.createElement('meta');
    if (name) {
      meta.setAttribute('name', name);
    }
    if (property) {
      meta.setAttribute('property', property);
    }
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

const ensureLinkTag = ({ rel, href, hreflang, dataRouteMeta }) => {
  if (!href) {
    return;
  }

  let selector = `link[rel="${rel}"]`;
  if (hreflang) {
    selector += `[hreflang="${hreflang}"]`;
  }

  let link = document.head.querySelector(selector);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    if (hreflang) {
      link.setAttribute('hreflang', hreflang);
    }
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
  if (dataRouteMeta) {
    link.setAttribute('data-route-meta', dataRouteMeta);
  }
};

const ensureStructuredData = (id, payload) => {
  let script = document.head.querySelector(`script[data-structured-data="${id}"]`);
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-structured-data', id);
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(payload);
};

const clearRouteAlternates = () => {
  document.head.querySelectorAll('link[data-route-meta="alternate"]').forEach((node) => node.remove());
};

const clearStructuredData = () => {
  document.head.querySelectorAll('script[data-structured-data]').forEach((node) => node.remove());
};

const isPrivateRoute = (pathname) => PRIVATE_ROUTE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

const buildHomeSchema = ({ title, description, url, locale }) => ([
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    email: SUPPORT_EMAIL,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: locale,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: SITE_NAME,
    url,
    operatingSystem: 'Windows, macOS, Linux',
    applicationCategory: 'SecurityApplication',
    description,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    url,
    description,
    isPartOf: SITE_URL,
    inLanguage: locale,
  },
]);

const buildContactSchema = ({ title, description, url, locale }) => ([
  {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: title,
    url,
    description,
    inLanguage: locale,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    email: SUPPORT_EMAIL,
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: SUPPORT_EMAIL,
        availableLanguage: supportedLanguages,
      },
    ],
  },
]);

function RouteMetadata() {
  const location = useLocation();
  const { t } = useTranslation();

  const metadata = useMemo(() => {
    const pathname = location.pathname || '/';
    const language = i18n.resolvedLanguage || i18n.language || 'en';
    const locale = LOCALE_MAP[language] || LOCALE_MAP.en;

    if (pathname === '/') {
      const title = `${t('home.hero.subtitle')} | ${SITE_NAME}`;
      const description = trimDescription(t('home.hero.description_long'));
      return {
        title,
        description,
        canonical: toAbsoluteUrl('/'),
        robots: INDEXABLE_ROBOTS,
        keywords: 'application allowlist, application whitelisting, trusted applications, endpoint security, zero trust, enterprise security, macOS, Windows, Linux',
        locale,
        type: 'website',
        schema: buildHomeSchema({
          title,
          description,
          url: toAbsoluteUrl('/'),
          locale: language,
        }),
      };
    }

    if (pathname === '/contact') {
      const title = `${t('contact.header.title')} | ${SITE_NAME}`;
      const description = trimDescription(`${t('contact.header.subtitle')} ${SUPPORT_EMAIL}`);
      return {
        title,
        description,
        canonical: toAbsoluteUrl('/contact'),
        robots: INDEXABLE_ROBOTS,
        keywords: 'allowtru contact, allowtru support, enterprise security support, application allowlist support',
        locale,
        type: 'website',
        schema: buildContactSchema({
          title,
          description,
          url: toAbsoluteUrl('/contact'),
          locale: language,
        }),
      };
    }

    if (pathname === '/login') {
      return {
        title: `${t('login.hero.subtitle')} | ${SITE_NAME}`,
        description: trimDescription(t('login.hero.description')),
        canonical: toAbsoluteUrl('/login'),
        robots: DEFAULT_ROBOTS,
        keywords: 'allowtru login',
        locale,
        type: 'website',
        schema: [],
      };
    }

    if (isPrivateRoute(pathname)) {
      return {
        title: `${SITE_NAME} Console`,
        description: 'Secure management console for enterprise application allowlist operations.',
        canonical: toAbsoluteUrl(pathname),
        robots: DEFAULT_ROBOTS,
        keywords: 'allowtru console',
        locale,
        type: 'website',
        schema: [],
      };
    }

    return {
      title: SITE_NAME,
      description: trimDescription(t('home.hero.description_long')),
      canonical: toAbsoluteUrl(pathname),
      robots: DEFAULT_ROBOTS,
      keywords: 'allowtru',
      locale,
      type: 'website',
      schema: [],
    };
  }, [location.pathname, t]);

  useEffect(() => {
    const language = i18n.resolvedLanguage || i18n.language || 'en';
    document.title = metadata.title;
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';

    ensureMetaTag({ name: 'description', content: metadata.description });
    ensureMetaTag({ name: 'keywords', content: metadata.keywords });
    ensureMetaTag({ name: 'robots', content: metadata.robots });
    ensureMetaTag({ name: 'googlebot', content: metadata.robots });
    ensureMetaTag({ name: 'application-name', content: SITE_NAME });
    ensureMetaTag({ property: 'og:site_name', content: SITE_NAME });
    ensureMetaTag({ property: 'og:title', content: metadata.title });
    ensureMetaTag({ property: 'og:description', content: metadata.description });
    ensureMetaTag({ property: 'og:url', content: metadata.canonical });
    ensureMetaTag({ property: 'og:type', content: metadata.type });
    ensureMetaTag({ property: 'og:locale', content: metadata.locale });
    ensureMetaTag({ name: 'twitter:card', content: 'summary_large_image' });
    ensureMetaTag({ name: 'twitter:title', content: metadata.title });
    ensureMetaTag({ name: 'twitter:description', content: metadata.description });

    ensureLinkTag({ rel: 'canonical', href: metadata.canonical });
    clearRouteAlternates();
    supportedLanguages.forEach((supportedLanguage) => {
      ensureLinkTag({
        rel: 'alternate',
        href: `${metadata.canonical}?lng=${encodeURIComponent(supportedLanguage)}`,
        hreflang: supportedLanguage,
        dataRouteMeta: 'alternate',
      });
    });
    ensureLinkTag({
      rel: 'alternate',
      href: metadata.canonical,
      hreflang: 'x-default',
      dataRouteMeta: 'alternate',
    });

    clearStructuredData();
    metadata.schema.forEach((item, index) => ensureStructuredData(`${location.pathname || '/'}-${index}`, item));
  }, [location.pathname, metadata]);

  return null;
}

export default RouteMetadata;
